import { MessageCircle, Search, ChevronLeft } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useWorkspaceNavigate as useNavigate } from '@/contexts/DocumentWorkspace';
import { useContactPanel } from '@/contexts/ContactPanelContext';
import { useAuth } from '@/hooks/useAuth';
import NotificationBell from './NotificationBell';

/**
 * AppHeader — slim top bar for the Scrivar desktop shell.
 *
 * variant="app"    left = section title + search trigger, right = contact + bell + avatar
 * variant="editor" left = "Scrivar / Home" back link,    right = contact + bell + avatar (no search)
 *
 * Renders inside a `.scrivar-desktop` root; uses `--ds-*` CSS variables.
 * No web-isms: no "Open app", "Pricing", or "Teams".
 */
export default function AppHeader({ variant = 'app', title }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { open: openContact } = useContactPanel();
  const { user, account } = useAuth();

  // Show back button on all sub-screens (not on /tools home)
  const showBack = variant === 'app' && location.pathname !== '/tools';

  const email = account?.email || user?.email || '?';
  const initial = email[0].toUpperCase();

  function openCommandPalette() {
    document.dispatchEvent(new CustomEvent('scrivar:open-command-palette'));
  }

  return (
    <header
      style={{
        height: '60px',
        flexShrink: 0,
        borderBottom: '1px solid var(--ds-line, var(--line, #EAEBF0))',
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        padding: '0 24px',
      }}
    >
      {/* Left side */}
      {variant === 'editor' ? (
        /* Editor back-link — navigate to /tools */
        <button
          type="button"
          onClick={() => navigate('/tools')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '13.5px',
            fontWeight: 500,
            color: 'var(--ds-ink-2, var(--ink-2, #565869))',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '0',
            fontFamily: 'inherit',
          }}
          aria-label="Back to Home"
        >
          <ChevronLeft size={16} />
          <span>Scrivar</span>
          <span style={{ color: 'var(--ds-ink-4, var(--ink-4, #A9ABB8))' }}>/</span>
          <span>Home</span>
        </button>
      ) : (
        <>
          {/* Back button — shown on every sub-screen (not /tools) */}
          {showBack && (
            <button
              type="button"
              onClick={() => navigate(-1)}
              aria-label="Back"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                background: 'var(--ds-sidebar, #F6F7F9)',
                border: '1px solid var(--ds-line-2, #DEDFE7)',
                color: 'var(--ds-ink-2, #565869)',
                cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              <ChevronLeft size={16} />
            </button>
          )}

          {/* Section title */}
          {title && (
            <div
              style={{
                fontSize: '18px',
                fontWeight: 600,
                letterSpacing: '-0.3px',
                color: 'var(--ds-ink, var(--ink, #191A24))',
                whiteSpace: 'nowrap',
              }}
            >
              {title}
            </div>
          )}

          {/* Search trigger — opens CommandPalette via custom event */}
          <button
            type="button"
            onClick={openCommandPalette}
            aria-label="Search tools, files or actions"
            style={{
              marginLeft: '18px',
              flex: 1,
              maxWidth: '380px',
              height: '36px',
              background: 'var(--ds-sidebar, var(--sidebar, #F6F7F9))',
              border: '1px solid var(--ds-line-2, var(--line-2, #DEDFE7))',
              borderRadius: '9px',
              display: 'flex',
              alignItems: 'center',
              gap: '9px',
              padding: '0 12px',
              color: 'var(--ds-ink-3, var(--ink-3, #8C8FA0))',
              fontSize: '13px',
              cursor: 'pointer',
              fontFamily: 'inherit',
              textAlign: 'left',
            }}
          >
            <Search size={16} aria-hidden="true" />
            <span style={{ flex: 1 }}>Search tools, files or actions</span>
            <span
              style={{
                fontSize: '11px',
                color: 'var(--ds-ink-4, var(--ink-4, #A9ABB8))',
                border: '1px solid var(--ds-line-2, var(--line-2, #DEDFE7))',
                borderRadius: '5px',
                padding: '1px 6px',
                fontFamily: 'monospace',
              }}
            >
              ⌘K
            </span>
          </button>
        </>
      )}

      {/* Right cluster: Contact us | NotificationBell | Avatar */}
      <div
        style={{
          marginLeft: 'auto',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
        }}
      >
        {/* Contact us button */}
        <button
          type="button"
          onClick={openContact}
          aria-label="Contact us"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            height: '36px',
            padding: '0 14px',
            borderRadius: '9px',
            background: 'var(--ds-indigo-50, var(--indigo-50, #EEF0FE))',
            color: 'var(--ds-indigo-strong, var(--indigo-strong, #4338CA))',
            border: '1px solid var(--ds-indigo-100, var(--indigo-100, #E2E5FC))',
            fontFamily: 'inherit',
            fontSize: '13px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          <MessageCircle size={16} aria-hidden="true" />
          Contact us
        </button>

        {/* Notification bell */}
        <NotificationBell />

        {/* Avatar — navigates to /account */}
        <button
          type="button"
          onClick={() => navigate('/account')}
          aria-label="Account"
          style={{
            width: '34px',
            height: '34px',
            borderRadius: '9px',
            background: 'linear-gradient(140deg, var(--ds-mark-grad-1, #5B53E8), var(--ds-mark-grad-2, #8B7FF0))',
            color: 'var(--ds-content, #fff)',
            fontWeight: 600,
            fontSize: '13px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            border: 'none',
            fontFamily: 'inherit',
          }}
        >
          {initial}
        </button>
      </div>
    </header>
  );
}
